import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import './CheckoutPage.css';
import { apiService, CHECKOUT_API_CONFIG } from '../../services/apiService';
import { collectDeviceSignals } from '../../services/deviceSignals';

// The calls below hit BEMI's own API host and the brand-free /api/gateway/... path instead of
// apiService's default api.moneyeu.com + /moneyEu/api/... base, but ONLY when this page is actually
// being served from a bemi.uk host. The decision lives in apiService (CHECKOUT_API_CONFIG) so that
// this page, the 3thix inline onramp (useThixOnramp) and the payment status page cannot disagree
// about which brand they're on — a checkout that fetched its order from BEMI but then polled status
// on MoneyEU would leak the brand halfway through the payment.
//
// It's `undefined` on a moneyeu.com host, so those calls fall through to apiService's untouched
// default: this changes behaviour for the BEMI-branded flow only, never for any other merchant.
const CHECKOUT_ALIAS_CONFIG = CHECKOUT_API_CONFIG;
import { API_PATHS } from '../../utils/apiPaths';
import { formatCardNumber, formatExpiryDate, formatCvv } from '../../utils/cardFormatting';
import { useThixOnramp } from './useThixOnramp';
import RapydInlineCheckout from './RapydInlineCheckout';
import { formatCurrencyAmount } from '../../utils/helpers';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { cn } from '../../lib/utils';
import PaymentStatusCard, {
  resolvePaymentStatusVariant,
} from '../payment/PaymentStatusCard';
import {
  captureAncestorOrigins,
  isEmbeddedCheckout,
  postCheckoutMessage,
  resolveFramedRedirectUrl,
} from './checkoutEmbed';
import {
  checkoutBrandClass,
  checkoutDir,
  checkoutNumberLocale,
  getCheckoutCopy,
  resolveCheckoutLocale,
} from './checkoutI18n';
import {
  BsShieldLockFill,
  BsLockFill,
  BsCurrencyBitcoin,
  BsArrowRepeat,
  BsExclamationTriangleFill,
} from 'react-icons/bs';

type PaymentScheme = {
  slug: string;
  label: string;
  /** True when this scheme came from the merchant's Bolt-on mapping rather than the primary
   * terminal: unused by rendering (both render as identical flat tiles), carried through in case
   * it's ever needed. */
  boltOn?: boolean;
};

/** A bank the shopper can pay from, for pay-by-bank (GET .../checkout/orders/{token}/banks). */
type CheckoutBank = {
  bankId: string;
  name: string;
  country?: string | null;
  bankGroupId?: string | null;
  bankGroupName?: string | null;
};

type OrderSummary = {
  orderId: string;
  amount: number;
  currency: string;
  merchantName: string | null;
  /** Merchant-configured trading name (Merchant.descriptorDisplayEnabled). Null when off, and
   *  then the merchantName line below is shown unchanged. Display only — it does not affect the
   *  billing descriptor on the cardholder's statement. */
  descriptorName: string | null;
  description: string | null;
  customerName: string | null;
  status: string;
  /** Final result from the order payload. "completed" or "failed" means this page must show the
   * status screen instead of the checkout form. */
  outcome?: string | null;
  cryptoPaymentAvailable?: boolean;
  /** "ONCHAIN" or "OFFCHAIN": which KuCoin Pay flow the Bolt-on mapping is configured for. Only
   * meaningful when cryptoPaymentAvailable is true. ONCHAIN renders an on-page deposit
   * address/QR instead of redirecting to KuCoin's hosted checkout page. */
  cryptoPaymentMode?: string;
  availablePaymentSchemes?: PaymentScheme[];
  /** True whenever this checkout's pinned acquirer is CollectNExchange at all: independent of
   * whether APM is enabled/availablePaymentSchemes is non-empty. CollectNExchange never collects
   * card details on our own form, APM enabled or not. */
  cardRedirectOnly?: boolean;
  /** False only when the pinned/primary terminal itself is KuCoin Pay (no "card" concept at all)
   *: hides the "Card" tile entirely instead of showing a redundant/broken option. Defaults to
   * true when absent. */
  cardAvailable?: boolean;
  /** True only when the pinned/primary terminal itself has the admin "Enable APM" checkbox on:
   * distinct from availablePaymentSchemes being non-empty, since that can also come from a
   * Bolt-on'd APM terminal alongside an unrelated primary card acquirer (Card stays visible then).
   * Hides the Card tile/form entirely, showing only APM scheme tiles, per explicit merchant request. */
  apmEnabled?: boolean;
  /** 3thix onramp this page can render inline — see useThixOnramp. */
  onrampAvailable?: boolean;
  /** Pay by bank: the pinned terminal is a pay-by-bank acquirer, so this page lists the shopper's
   * banks and sends the chosen one as selectedBank. No card option exists then. */
  bankSelectionAvailable?: boolean;
  /** Collect Group inline (Rapyd): collect the shopper's tax id + date of birth on this page and
   * send them with the pay call, so Collect Group can submit the AML profile (can_pay). */
  collectInlineIdentity?: boolean;
  /** Collect Group "Test dummy values" (dev only): skip the tax ID / DOB step — the backend
   * sends placeholder values itself on the pay call. */
  testDummyValues?: boolean;
  /** Populated only when cryptoPaymentMode is ONCHAIN and the shopper already has a still-pending
   * deposit in flight for this order: lets a page refresh restore the same panel instead of
   * losing the address/amount/QR, which only ever came back once in the pay-crypto response. */
  onchainTransactionId?: string | null;
  onchainAddress?: string | null;
  onchainChain?: string | null;
  onchainCurrencyCode?: string | null;
  onchainAmount?: string | null;
  onchainExpireTime?: number | null;
  onchainQrCodeUrl?: string | null;
  /** currency -> chain list, queried live from KuCoin: never from admin config. More than one
   * currency key, or more than one chain for the selected currency, is when the checkout page
   * shows a picker before "Get Deposit Address". */
  cryptoOnchainCurrencyOptions?: Record<string, string[]>;
  /** True only when the merchant's Bolt-on mapping resolves to some HPP acquirer that's neither
   * KuCoin Pay (cryptoPaymentAvailable) nor a CollectNExchange APM terminal (folded into
   * availablePaymentSchemes instead): shows one generic "Alternative Payment" tile. */
  boltOnAvailable?: boolean;
  /** Must be echoed back on POST .../capture-origin: see the backend's Order.originCaptureNonce
   * javadoc for why. Null once a capture has already succeeded or the order left the capture
   * window; in either case there's nothing left to send it for. */
  originCaptureNonce?: string | null;
  /** True only when the pinned MerchantAcquirerMapping has collectCustomerDetailsOnCheckout
   * switched on: this page must render its own billing-details form and submit them with the
   * card payment, since the merchant's processPayment call isn't required to send them for this
   * mapping. See MerchantAcquirerMapping.collectCustomerDetailsOnCheckout's javadoc. */
  collectCustomerDetailsOnCheckout?: boolean;
  /** When true, show extra "Latin characters" name/address fields; the local-language main fields
   * are kept for record and the Latin fields are forwarded to the acquirer. */
  latinSupportEnabled?: boolean;
  /** The single language this checkout should render in: see the backend's
   * Merchant.translationEnabled/translateLanguage javadoc. Absent/null means default (English). */
  translateLanguage?: string | null;
  /** See the backend's AcquirerTerminal.notes javadoc: free-text admin note from the pinned
   * terminal. Null/blank means no footer at all. */
  terminalNotes?: string | null;
};

function isTerminalCheckoutOutcome(outcome?: string | null): boolean {
  const value = String(outcome ?? '').trim().toLowerCase();
  return value === 'completed' || value === 'failed';
}

type PaymentResult = {
  transactionId: string;
  status: string;
  amount: number | null;
  currency: string | null;
  message: string | null;
  reason: string | null;
  acquirerReference: string | null;
  merchantRedirectUrl: string | null;
  redirectTimerSeconds: number | null;
  logoUrl: string | null;
  /** KuCoin Pay on-chain only: PART_PAYMENT/FULL_PAYMENT/OVER_PAYMENT/WRONG_PAYMENT, null for
   * every other flow/status. */
  cryptoPaymentStatus: string | null;
  cryptoReceivedAmount: string | null;
  cryptoRequiredAmount: string | null;
};

type LoadState = 'loading' | 'ready' | 'invalid';

/** Populated from /pay-crypto's response when cryptoPaymentMode is ONCHAIN: a raw deposit
 * address/amount to render on this page, never a redirect. */
type OnchainCryptoDetails = {
  transactionId: string;
  address: string;
  chain: string | null;
  currency: string | null;
  amount: string | null;
  expireTime: number | null;
  qrCodeUrl: string | null;
};

// Used only when the merchant never set a redirect timer (Merchant.redirectTimerSeconds is null)
//: otherwise the real value from the response wins, see resolvePostPaymentRedirect below.
const AUTO_REDIRECT_SECONDS = 5;

/** mm:ss remaining until expireTime, or null once it's passed: used for the on-chain deposit's
 * live countdown (ticked every second via onchainNow, not computed once and left static). */
function formatCountdown(expireTime: number, now: number): string | null {
  const remainingMs = expireTime - now;
  if (remainingMs <= 0) return null;
  const totalSeconds = Math.floor(remainingMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/** required - received, trimmed of trailing zeros: null when either value is missing/invalid or
 * the deposit already meets/exceeds the required amount. */
function formatRemainingAmount(required: string | null, received: string | null): string | null {
  if (!required || !received) return null;
  const requiredNum = Number(required);
  const receivedNum = Number(received);
  if (!Number.isFinite(requiredNum) || !Number.isFinite(receivedNum)) return null;
  const remaining = requiredNum - receivedNum;
  if (remaining <= 0) return null;
  return remaining.toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
}

/** Whole amounts stay whole (1000). A fractional amount keeps two decimals (1000.5 → 1000.50). */
function checkoutAmountDigits(amount: number | null | undefined): {
  minimumFractionDigits: number;
  maximumFractionDigits: number;
} {
  const numeric = Number(amount);
  const hasFraction = Number.isFinite(numeric) && !Number.isInteger(numeric);
  return hasFraction
    ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
    : { minimumFractionDigits: 0, maximumFractionDigits: 0 };
}

function formatCheckoutMoney(
  amount: number | null | undefined,
  currency: string | null | undefined,
  options: { locale?: string; includeCode?: boolean } = {},
): string {
  return formatCurrencyAmount(Number(amount), currency ?? 'USD', {
    locale: options.locale,
    includeCode: options.includeCode,
    ...checkoutAmountDigits(amount),
  });
}

function unwrapPaymentResult(res: unknown, summary: OrderSummary | null): PaymentResult {
  let data: Record<string, unknown> =
    res && typeof res === 'object' ? (res as Record<string, unknown>) : {};

  if (data.data && typeof data.data === 'object') {
    data = data.data as Record<string, unknown>;
  }

  const readNumber = (value: unknown): number | null =>
    value === undefined || value === null || value === '' ? null : Number(value);
  const readString = (value: unknown): string | null =>
    value === undefined || value === null ? null : String(value);

  return {
    transactionId: String(
      data.transactionId ?? data.orderId ?? summary?.orderId ?? '—',
    ),
    // The initial POST /checkout/orders/{token}/pay response uses "transactionStatus"
    // (PaymentProcessResponse) or "status" (CheckoutPaymentResultResponse, test mode); the
    // follow-up GET status-endpoint response (PaymentStatusResponse) uses "status": checking
    // both covers every response shape this function is ever fed.
    status: String(
      data.status ?? data.transactionStatus ?? 'PROCESSING',
    ),
    amount: readNumber(data.amount) ?? summary?.amount ?? null,
    currency: readString(data.currency) ?? summary?.currency ?? null,
    message: readString(data.message),
    reason: readString(data.reason),
    acquirerReference: readString(data.acquirerReference),
    // Only ever present on the status-endpoint response (PaymentStatusResponse.merchantRedirectUrl)
    //: never on the initial pay-response, which is exactly why this needs a follow-up fetch (see
    // the effect below) rather than trusting whatever the initial POST returned.
    merchantRedirectUrl: readString(data.merchantRedirectUrl),
    redirectTimerSeconds: readNumber(data.redirectTimerSeconds),
    logoUrl: readString(data.logoUrl),
    cryptoPaymentStatus: readString(data.cryptoPaymentStatus),
    cryptoReceivedAmount: readString(data.cryptoReceivedAmount),
    cryptoRequiredAmount: readString(data.cryptoRequiredAmount),
  };
}

function resolvePostPaymentRedirect(
  result: PaymentResult | null,
  variant: 'success' | 'error' | 'pending',
) {
  if (!result || variant === 'pending') return undefined;

  const redirectUrl = result.merchantRedirectUrl?.trim();
  if (!redirectUrl) return undefined;

  const timerSeconds =
    result.redirectTimerSeconds && result.redirectTimerSeconds > 0
      ? result.redirectTimerSeconds
      : AUTO_REDIRECT_SECONDS;

  return {
    timerSeconds,
    redirectUrl,
  };
}

// A completed/declined payment's redirectUrl very often points at OUR OWN status page
// (PaymentRedirectStatusPage, built by the backend with no idea whether this session is
// embedded) rather than a third-party acquirer's hosted page: carrying parentOrigin forward
// only in that same-origin case is what lets that page know it's embedded and should post
// moneyeu.checkout.result instead of just rendering standalone. A genuine third-party acquirer
// URL (3DS challenge, KuCoin, CollectNExchange) is left untouched: appending our own query
// param to someone else's URL could break it, and per the docs that page returns to our own
// status page on its own once it resolves anyway.
function navigateForRedirect(url: string): void {
  const parentOrigin = new URLSearchParams(window.location.search).get('parentOrigin');
  if (parentOrigin) {
    try {
      const target = new URL(url, window.location.origin);
      if (target.origin === window.location.origin) {
        target.searchParams.set('parentOrigin', parentOrigin);
        window.location.href = target.toString();
        return;
      }
    } catch {
      // Malformed URL: fall through to a plain navigation below.
    }
  }
  window.location.href = url;
}

const fieldIconClassName =
  'pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground';

type CheckoutFloatFieldProps = React.InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  error?: string;
  hint?: string;
};

/** Label stays inside the box, above the value, so an empty field still names itself. */
function CheckoutFloatField({ label, error, hint, id, ...inputProps }: CheckoutFloatFieldProps) {
  const autoId = React.useId();
  const fieldId = id ?? autoId;

  return (
    <div className="checkout-float">
      <div className={cn('checkout-float-box', error && 'is-error')}>
        <Label htmlFor={fieldId} className="checkout-float-label">
          {label}
        </Label>
        <Input id={fieldId} className="checkout-float-input" {...inputProps} />
      </div>
      {hint ? <p className="checkout-float-hint">{hint}</p> : null}
      {error ? <p className="checkout-field-err">{error}</p> : null}
    </div>
  );
}

function LockGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
      <rect x="3" y="7" width="10" height="7.2" rx="1.4" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M5.2 7V5.1a2.8 2.8 0 0 1 5.6 0V7" fill="none" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}

function CardBrandMarks() {
  const overlapId = `mc-overlap-${React.useId().replace(/:/g, '')}`;

  return (
    <div className="checkout-brands" aria-hidden="true">
      <svg className="checkout-brand-visa" viewBox="58 140 664 220" role="img" aria-label="Visa">
        <path
          fill="#1434CB"
          d="M489.823 143.111C442.988 143.111 401.134 167.393 401.134 212.256C401.134 263.706 475.364 267.259 475.364 293.106C475.364 303.989 462.895 313.731 441.6 313.731C411.377 313.731 388.789 300.119 388.789 300.119L379.123 345.391C379.123 345.391 405.145 356.889 439.692 356.889C490.898 356.889 531.19 331.415 531.19 285.784C531.19 231.419 456.652 227.971 456.652 203.981C456.652 195.455 466.887 186.114 488.122 186.114C512.081 186.114 531.628 196.014 531.628 196.014L541.087 152.289C541.087 152.289 519.818 143.111 489.823 143.111ZM61.3294 146.411L60.1953 153.011C60.1953 153.011 79.8988 156.618 97.645 163.814C120.495 172.064 122.122 176.868 125.971 191.786L167.905 353.486H224.118L310.719 146.411H254.635L198.989 287.202L176.282 167.861C174.199 154.203 163.651 146.411 150.74 146.411H61.3294ZM333.271 146.411L289.275 353.486H342.756L386.598 146.411H333.271ZM631.554 146.411C618.658 146.411 611.825 153.318 606.811 165.386L528.458 353.486H584.542L595.393 322.136H663.72L670.318 353.486H719.805L676.633 146.411H631.554ZM638.848 202.356L655.473 280.061H610.935L638.848 202.356Z"
        />
      </svg>
      <svg className="checkout-brand-mc" viewBox="0 0 38 24" role="img" aria-label="Mastercard">
        <defs>
          <clipPath id={overlapId}>
            <circle cx="15" cy="12" r="10" />
          </clipPath>
        </defs>
        <circle cx="15" cy="12" r="10" fill="#EB001B" />
        <circle cx="23" cy="12" r="10" fill="#F79E1B" />
        <circle cx="23" cy="12" r="10" fill="#FF5F00" clipPath={`url(#${overlapId})`} />
      </svg>
    </div>
  );
}

function CheckoutTrustRow({ label }: { label: string }) {
  return (
    <div className="checkout-trust-row">
      <span className="checkout-trust-lock">
        <LockGlyph />
        {label}
      </span>
      <CardBrandMarks />
    </div>
  );
}

type CheckoutLineFieldProps = React.InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  error?: string;
  trailing?: React.ReactNode;
};

/** Outlined field: the label sits on the top border, as on the hosted card form. */
function CheckoutOutlineField({ label, error, id, ...inputProps }: Omit<CheckoutLineFieldProps, 'trailing'>) {
  const autoId = React.useId();
  const fieldId = id ?? autoId;
  const errorId = `${fieldId}-error`;

  return (
    <div className="checkout-outline">
      <div className={cn('checkout-outline-box', error && 'is-error')}>
        <label htmlFor={fieldId} className="checkout-outline-label">
          {label}
        </label>
        <input
          id={fieldId}
          className="checkout-outline-input"
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          {...inputProps}
        />
      </div>
      {error ? <p id={errorId} className="checkout-field-err">{error}</p> : null}
    </div>
  );
}

/** Label above the box, placeholder inside — expiry, security code, cardholder name. */
function CheckoutStackedField({ label, error, id, trailing, ...inputProps }: CheckoutLineFieldProps) {
  const autoId = React.useId();
  const fieldId = id ?? autoId;
  const errorId = `${fieldId}-error`;

  return (
    <div className="checkout-stack">
      <label htmlFor={fieldId} className="checkout-stack-label">
        {label}
      </label>
      <div className={cn('checkout-stack-box', error && 'is-error')}>
        <input
          id={fieldId}
          className="checkout-stack-input"
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          {...inputProps}
        />
        {trailing}
      </div>
      {error ? <p id={errorId} className="checkout-field-err">{error}</p> : null}
    </div>
  );
}

const CheckoutPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const embedded = isEmbeddedCheckout();
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [loadError, setLoadError] = useState<string>('');
  const [summary, setSummary] = useState<OrderSummary | null>(null);
  const onramp = useThixOnramp(token);
  /** The identity stages render a multi-field form; the default 420px card forces the shopper to
   *  scroll through it. Widening only for those keeps the ordinary card checkout unchanged. */
  const onrampFormOpen = Boolean(summary?.onrampAvailable)
    && onramp.phase !== 'idle'
    && onramp.phase !== 'error';
  const [onrampForm, setOnrampForm] =
    useState({ legalName: '', phoneNumber: '', country: '' });
  const [kycValues, setKycValues] = useState<Record<string, string>>({});
  const [profile, setProfile] = useState({
    given_name: '', surname: '', line1: '', city: '',
    postal_code: '', state: '', dob: '', ssn: '', country: '',
  });
  const locale = resolveCheckoutLocale(summary?.translateLanguage);
  const t = getCheckoutCopy(locale);

  const [form, setForm] = useState({ cardNumber: '', cardholderName: '', expiryDate: '', cvv: '' });
  const [orderDetailsOpen, setOrderDetailsOpen] = useState(false);
  const [orderIdCopied, setOrderIdCopied] = useState(false);
  // Only rendered/required when summary.collectCustomerDetailsOnCheckout is true: see
  // OrderSummary.collectCustomerDetailsOnCheckout's javadoc.
  const [billingForm, setBillingForm] = useState({
    customerName: '',
    address: '',
    customerNameLatin: '',
    addressLatin: '',
    city: '',
    state: '',
    zip: '',
    countryName: '',
    phone: '',
  });
  // Set once the pay call succeeds for a Collect Group inline order: swaps the form for Rapyd's
  // card frame mounted right here in the card, instead of navigating away to a backend page.
  const [inlineRapyd, setInlineRapyd] = useState<{ transactionId: string; paymentUrl: string } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  // Full-screen loader shown while we hand the shopper off to an external acquirer redirect
  // (e.g. Axipays S2S 3DS/bank). The acquirer's own redirect endpoint can paint a blank frame
  // before it forwards to the bank page; painting this overlay just before navigating means the
  // browser holds a loader as its last frame instead of the frozen form / a blank. See
  // navigateWithRedirectLoader.
  const [redirecting, setRedirecting] = useState(false);
  const [paid, setPaid] = useState(false);
  const [paymentResult, setPaymentResult] = useState<PaymentResult | null>(null);
  const [payError, setPayError] = useState<string>('');
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState('');

  const [awaitingOtp, setAwaitingOtp] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');

  const [paymentMethod, setPaymentMethod] = useState<'card' | 'crypto' | 'bolt_on' | 'bank'>('card');
  const [boltOnSubmitting, setBoltOnSubmitting] = useState(false);
  const [boltOnError, setBoltOnError] = useState('');
  // Pay by bank: the bank list is loaded the first time the method is shown. banks === null means
  // still loading; banksFailed means none came back, so the shopper picks on the provider's page.
  const [banks, setBanks] = useState<CheckoutBank[] | null>(null);
  const [banksFailed, setBanksFailed] = useState(false);
  const [bankQuery, setBankQuery] = useState('');
  const [selectedBankId, setSelectedBankId] = useState<string | null>(null);
  const [bankSubmitting, setBankSubmitting] = useState(false);
  const [bankError, setBankError] = useState('');
  const [cryptoSubmitting, setCryptoSubmitting] = useState(false);
  const [cryptoError, setCryptoError] = useState('');
  const [onchainDetails, setOnchainDetails] = useState<OnchainCryptoDetails | null>(null);
  const [onchainCopied, setOnchainCopied] = useState(false);
  const [onchainNow, setOnchainNow] = useState<number>(() => Date.now());
  // Set while the deposit has received something but not the full amount yet (PART_PAYMENT) or
  // the wrong asset (WRONG_PAYMENT): the transaction stays PROCESSING/pending server-side, so
  // polling keeps running and no redirect happens; the shopper just sees this banner update.
  const [underpaymentInfo, setUnderpaymentInfo] = useState<{
    status: string;
    received: string | null;
    required: string | null;
  } | null>(null);
  // Set for a brief window when OVER_PAYMENT is detected, before flipping to the final success
  // view: gives the shopper a moment to see "overpayment received" instead of an abrupt jump.
  const [overpaymentInfo, setOverpaymentInfo] = useState<{
    received: string | null;
    required: string | null;
  } | null>(null);
  // Guards against the poll interval re-scheduling a second 5s timer before the first one fires.
  const overpaymentTimerStarted = useRef(false);
  // Only meaningful when summary.cryptoOnchainCurrencyOptions has more than one currency/chain:
  // otherwise both stay null and the backend just uses KuCoin's own live default.
  const [selectedCurrency, setSelectedCurrency] = useState<string | null>(null);
  const [selectedChain, setSelectedChain] = useState<string | null>(null);

  useEffect(() => {
    const disableContextMenu = (event: MouseEvent) => {
      event.preventDefault();
    };
    document.addEventListener('contextmenu', disableContextMenu);
    return () => document.removeEventListener('contextmenu', disableContextMenu);
  }, []);

  // Live countdown: ticks every second while a deposit is awaiting payment, so "Expires in" is
  // an actual moving clock instead of a static one-time label.
  useEffect(() => {
    if (!onchainDetails?.expireTime || paid) return;
    const interval = window.setInterval(() => setOnchainNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [onchainDetails?.expireTime, paid]);

  // Pre-select the first currency once the order summary loads, so the picker never starts with
  // nothing chosen: the shopper can still change it before clicking "Get Deposit Address".
  useEffect(() => {
    const options = summary?.cryptoOnchainCurrencyOptions;
    const currencies = options ? Object.keys(options) : [];
    if (currencies.length > 0 && !selectedCurrency) {
      setSelectedCurrency(currencies[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary?.cryptoOnchainCurrencyOptions]);

  // Re-derive the chain choice whenever the selected currency changes: resets to that currency's
  // first live-reported chain rather than carrying over a chain that may not exist for it.
  useEffect(() => {
    if (!selectedCurrency) return;
    const chains = summary?.cryptoOnchainCurrencyOptions?.[selectedCurrency] ?? [];
    setSelectedChain(chains.length > 0 ? chains[0] : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCurrency, summary?.cryptoOnchainCurrencyOptions]);

  // paymentMethod defaults to 'card' before the order summary loads: when cardAvailable turns
  // out false (KuCoin as the actual primary/pinned terminal, not just Bolt-on'd) OR the pinned
  // terminal has APM enabled, 'card' is never in paymentMethodOptions at all, so
  // renderMethodBody('card') would otherwise still render a real (and broken/absent) card form.
  // Switches to whatever the first actually-available option is.
  useEffect(() => {
    // Mirrors paymentMethodOptions' own cardHiddenForApm logic below: duplicated inline since this
    // effect runs before that later const is declared in the component body.
    const hasApmSchemes = Boolean(summary?.availablePaymentSchemes && summary.availablePaymentSchemes.length > 0);
    const cardHidden = summary?.cardAvailable === false || (Boolean(summary?.apmEnabled) && hasApmSchemes);
    if (!summary || !cardHidden || paymentMethod !== 'card') return;
    if (summary.bankSelectionAvailable) {
      setPaymentMethod('bank');
    } else if (summary.cryptoPaymentAvailable) {
      setPaymentMethod('crypto');
    } else if (summary.availablePaymentSchemes && summary.availablePaymentSchemes.length > 0) {
      setSelectedScheme(summary.availablePaymentSchemes[0].slug);
    } else if (summary.boltOnAvailable) {
      setPaymentMethod('bolt_on');
    }
  }, [summary, paymentMethod]);

  useEffect(() => {
    if (paymentMethod !== 'bank' || banks !== null || !token) return;
    let cancelled = false;
    (async () => {
      try {
        const res: any = await apiService.get(`/checkout/orders/${token}/banks`, CHECKOUT_ALIAS_CONFIG);
        const list: CheckoutBank[] = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        if (cancelled) return;
        setBanks(list);
        setBanksFailed(list.length === 0);
      } catch {
        if (cancelled) return;
        setBanks([]);
        setBanksFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [paymentMethod, banks, token]);

  // Non-null only when the shopper picked an Alternative Payment Method (see
  // paymentMethodOptions/handlePaymentMethodChange below): its value is the scheme slug
  // (e.g. "ideal"), sent as CheckoutPaymentRequest.paymentScheme instead of card fields.
  const [selectedScheme, setSelectedScheme] = useState<string | null>(null);

  // True whenever the pinned acquirer is CollectNExchange at all (see
  // PaymentServiceImpl.isCollectNExchangeTerminal): independent of whether APM is enabled/
  // availablePaymentSchemes is non-empty. CollectNExchange collects card details on ITS OWN
  // hosted page for every method, card included, never ours: so the "Card" tile always sits
  // alongside whatever APM schemes are offered (or alone, if APM is off) and always redirects
  // rather than showing our own card-number form. Any other acquirer (real S2S card collection)
  // keeps showing the card form as normal.
  const cardRedirectOnly = Boolean(summary?.cardRedirectOnly);

  useEffect(() => {
    if (!token) {
      setLoadState('invalid');
      setLoadError(t.missingCheckoutLink);
      return;
    }
    (async () => {
      try {
        const res: any = await apiService.get(`/checkout/orders/${token}`, CHECKOUT_ALIAS_CONFIG);
        const data: OrderSummary = res?.data ?? res;
        setSummary(data);
        // A page refresh mid on-chain-deposit has no redirect to land back on: restore the exact
        // same address/amount/QR panel from what's already persisted, instead of losing it.
        if (data.onchainAddress && data.onchainTransactionId) {
          setPaymentMethod('crypto');
          setOnchainDetails({
            transactionId: data.onchainTransactionId,
            address: data.onchainAddress,
            chain: data.onchainChain ?? null,
            currency: data.onchainCurrencyCode ?? null,
            amount: data.onchainAmount ?? null,
            expireTime: data.onchainExpireTime ?? null,
            qrCodeUrl: data.onchainQrCodeUrl ?? null,
          });
        }
        setLoadState('ready');
        if (data.originCaptureNonce) {
          captureAncestorOrigins(token, data.originCaptureNonce, CHECKOUT_ALIAS_CONFIG);
        }
      } catch (err: any) {
        setLoadError(err.response?.data?.message || t.invalidCheckoutLink);
        setLoadState('invalid');
      }
    })();
  }, [token]);

  useEffect(() => {
    const previousLang = document.documentElement.lang;
    const previousDir = document.documentElement.dir;
    document.documentElement.lang = locale;
    document.documentElement.dir = checkoutDir(locale);
    return () => {
      document.documentElement.lang = previousLang;
      document.documentElement.dir = previousDir;
    };
  }, [locale]);

  const resizeFrozenRef = useRef(false);
  useEffect(() => {
    const freeze = onramp.phase === 'verifying-document';
    // On the way in, ask once for room: Stripe's photo-ID modal needs roughly 760px, and a frame
    // auto-sized to the card would otherwise be frozen too short for it. Never smaller than the
    // frame already is, so a merchant who gave it a tall fixed height is unaffected.
    if (freeze && !resizeFrozenRef.current && embedded) {
      postCheckoutMessage({
        type: 'CHECKOUT_RESIZE',
        height: Math.max(760, document.documentElement.clientHeight),
      });
    }
    resizeFrozenRef.current = freeze;
  }, [onramp.phase, embedded]);

  useEffect(() => {
    if (!embedded) return;
    document.documentElement.classList.add('checkout-embedded');
    postCheckoutMessage({ type: 'CHECKOUT_READY' });

    const publishHeight = () => {
      // Stripe's document check opens a full-height, position:fixed overlay. Measured inside an
      // iframe, that overlay is as tall as the frame itself, so a merchant page that sizes the
      // frame from these messages grows it, which grows the overlay, which grows the next report —
      // and the modal ends up centred far below the fold. Hold the last height while it is open.
      if (resizeFrozenRef.current) return;
      postCheckoutMessage({
        type: 'CHECKOUT_RESIZE',
        height: Math.max(document.documentElement.scrollHeight, document.body.scrollHeight),
      });
    };
    publishHeight();
    const observer = new ResizeObserver(publishHeight);
    observer.observe(document.documentElement);
    window.addEventListener('resize', publishHeight);
    return () => {
      document.documentElement.classList.remove('checkout-embedded');
      observer.disconnect();
      window.removeEventListener('resize', publishHeight);
    };
  }, [embedded]);

  useEffect(() => {
    if (!embedded || loadState !== 'invalid') return;
    postCheckoutMessage({ type: 'CHECKOUT_ERROR', message: loadError });
  }, [embedded, loadState, loadError]);

  // Hands a completed 3thix onramp payment to the same result state the card flow uses.
  //
  // The onramp finishes inside its own hook, so without this `paid` never flips for it — and every
  // downstream behaviour keys off `paid`: the status card, the post-payment status refresh, the
  // documented `moneyeu.checkout.result` message merchants listen for, and the internal
  // PAYMENT_RESULT for our own iframe component. A 3thix payment inside a merchant's iframe was
  // therefore finishing without the merchant ever being told. Joining the shared path instead of
  // posting messages from here keeps one implementation of all of that.
  //
  // Seeded as PROCESSING, not a success: the charge is only final once the provider's webhook or
  // ThixReconciliationScheduler marks the transaction. The status card then polls
  // /payments/redirect/status for the real outcome, and the merchant message fires only once that
  // outcome is final — the same rule the card flow already follows.
  useEffect(() => {
    if (onramp.phase !== 'paid' || paid || !onramp.start?.externalOrderId) return;
    setPaymentResult({
      transactionId: onramp.start.externalOrderId,
      status: 'PROCESSING',
      amount: summary?.amount ?? null,
      currency: summary?.currency ?? null,
      message: null,
      reason: null,
      acquirerReference: null,
      merchantRedirectUrl: null,
      redirectTimerSeconds: null,
      logoUrl: null,
      cryptoPaymentStatus: null,
      cryptoReceivedAmount: null,
      cryptoRequiredAmount: null,
    });
    setPaid(true);
  }, [onramp.phase, onramp.start?.externalOrderId, paid, summary?.amount, summary?.currency]);

  useEffect(() => {
    if (!embedded || !paid || !paymentResult) return;
    postCheckoutMessage({
      type: 'PAYMENT_RESULT',
      transactionId: paymentResult.transactionId === '—' ? null : paymentResult.transactionId,
      status: paymentResult.status,
      message: paymentResult.message,
    });
  }, [embedded, paid, paymentResult]);

  const set = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const setBilling = (field: string, value: string) => {
    setBillingForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validate = (): boolean => {
    // An Alternative Payment Method selection has no card fields at all: the shopper enters
    // their details on the acquirer's own hosted page instead, so the card-field checks below
    // don't apply. Same for the "Card" tile itself whenever this checkout is pinned to
    // CollectNExchange (APM enabled or not): it collects card details on its own page too.
    // Collect Group inline / test dummy: identity is collected on Collect Group's hosted page (or
    // supplied by the backend for test dummy values) — nothing to validate on our checkout form.
    if (summary?.collectInlineIdentity) return true;

    if (selectedScheme || cardRedirectOnly) return true;

    const e: Record<string, string> = {};
    const cardDigits = form.cardNumber.replace(/\s/g, '');
    if (!cardDigits) e.cardNumber = t.cardNumberRequired;
    else if (cardDigits.length < 13 || cardDigits.length > 19) e.cardNumber = t.cardNumberInvalid;
    if (!form.cardholderName.trim()) e.cardholderName = t.nameRequired;
    if (!form.expiryDate.trim()) e.expiryDate = t.required;
    else if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(form.expiryDate.trim())) e.expiryDate = t.expiryFormat;
    if (!form.cvv.trim()) e.cvv = t.required;
    else if (!/^\d{3,4}$/.test(form.cvv.trim())) e.cvv = t.cvvInvalid;
    if (summary?.collectCustomerDetailsOnCheckout) {
      if (!billingForm.customerName.trim()) e.customerName = t.nameRequired;
      if (!billingForm.address.trim()) e.address = t.required;
      // Latin support: the Latin name/address are what's forwarded to the acquirer, so required.
      if (summary?.latinSupportEnabled) {
        if (!billingForm.customerNameLatin.trim()) e.customerNameLatin = t.nameRequired;
        if (!billingForm.addressLatin.trim()) e.addressLatin = t.required;
      }
      if (!billingForm.city.trim()) e.city = t.required;
      if (!billingForm.state.trim()) e.state = t.required;
      if (!billingForm.zip.trim()) e.zip = t.required;
      if (!billingForm.countryName.trim()) e.countryName = t.required;
      if (!billingForm.phone.trim()) e.phone = t.required;
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Paint the full-screen redirect loader, then navigate on the next frame. The double rAF lets
  // the overlay actually paint before window.location is set, so the browser keeps the loader as
  // its last frame during the external acquirer handoff (e.g. Axipays' redirect endpoint) instead
  // of showing the frozen form or a blank page until the bank page renders.
  const navigateWithRedirectLoader = (url: string): void => {
    setRedirecting(true);
    requestAnimationFrame(() => requestAnimationFrame(() => navigateForRedirect(url)));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setPayError('');
    try {
      // Best-effort browser risk signals (bot verdict + device fingerprint) — never blocks checkout;
      // resolves to {} if collection fails. See services/deviceSignals.
      const deviceSignals = await collectDeviceSignals();
      const payBody = selectedScheme
        ? { paymentScheme: selectedScheme }
        // "Card" under CollectNExchange (APM enabled or not): no paymentScheme, no card fields:
        // CollectNExchange itself collects card details on its own hosted page.
        : cardRedirectOnly
          ? // Redirect acquirers (including Collect Group): card/identity on the acquirer page.
            {}
          : {
              cardNumber: form.cardNumber.replace(/\s/g, ''),
              cardholderName: form.cardholderName.trim(),
              expiryDate: form.expiryDate.trim(),
              cvv: form.cvv.trim(),
              ...(summary?.collectCustomerDetailsOnCheckout
                ? {
                    // Latin support: forward the LATIN name/address to the acquirer and keep the
                    // local-language (main-field) values as *Native for record. Without Latin
                    // support, the main fields are forwarded as usual.
                    customerName: (summary?.latinSupportEnabled
                      ? billingForm.customerNameLatin
                      : billingForm.customerName).trim(),
                    address: (summary?.latinSupportEnabled
                      ? billingForm.addressLatin
                      : billingForm.address).trim(),
                    ...(summary?.latinSupportEnabled
                      ? {
                          customerNameNative: billingForm.customerName.trim(),
                          addressNative: billingForm.address.trim(),
                        }
                      : {}),
                    city: billingForm.city.trim(),
                    state: billingForm.state.trim(),
                    zip: billingForm.zip.trim(),
                    countryName: billingForm.countryName.trim(),
                    phone: billingForm.phone.trim(),
                  }
                : {}),
            };
      const res: any = await apiService.post(
        `/checkout/orders/${token}/pay`,
        { ...payBody, ...deviceSignals },
        // CollectNExchange's /pay makes four sequential Collect Group round-trips (register payer,
        // AML profile, payer status, order create) and runs ~18-20s; the default 30s axios timeout
        // is too tight for a slow sandbox response, which surfaced as a false "Payment failed".
        { ...CHECKOUT_ALIAS_CONFIG, timeout: 90000 },
      );
      // hpp=true (CollectNExchange APM, and any other hosted-checkout-redirect acquirer) carries
      // its redirect in paymentUrl, not redirectUrl: see PaymentServiceImpl.buildPaymentProcessResponse,
      // which only ever populates redirectUrl for the non-hpp/direct-acquirer-redirect case.
      const redirectUrl: string | undefined = res?.data?.hpp ? res?.data?.paymentUrl : res?.data?.redirectUrl;
      // Collect Group inline: don't navigate to the backend embed page — mount Rapyd's card frame
      // in this same card. paymentUrl (the masked checkout URL) is kept for the explicit fallback button.
      if (summary?.collectInlineIdentity && res?.data?.transactionId && res?.data?.paymentUrl) {
        setInlineRapyd({ transactionId: res.data.transactionId, paymentUrl: res.data.paymentUrl });
      } else if (res?.data?.otpRequired) {
        setAwaitingOtp(true);
      } else if (redirectUrl) {
        // Every live (non-sandbox) S2S response carries a redirectUrl: either a real acquirer
        // 3DS/challenge step (masked behind our own /payments/redirect/{transactionId} page,
        // which iframes the challenge and returns here via the acquirer's return_url once it
        // resolves) or, when no challenge is needed, our own status page directly. Navigating
        // there in both cases keeps this one path instead of duplicating the status-card/
        // auto-redirect logic that page already owns.
        // Stay on the current origin for `/moneyEu/*` so the iframe hits the API proxy instead
        // of the SPA host (that mismatch is what 404'd 3DS inside the dev iframe).
        navigateWithRedirectLoader(resolveFramedRedirectUrl(redirectUrl));
      } else {
        setPaymentResult(unwrapPaymentResult(res, summary));
        setPaid(true);
      }
    } catch (err: any) {
      setPayError(err.response?.data?.message || t.paymentFailed);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCryptoPay = async () => {
    setCryptoSubmitting(true);
    setCryptoError('');
    try {
      const res: any = await apiService.post(
        `/checkout/orders/${token}/pay-crypto`,
        {
          ...(selectedCurrency ? { currency: selectedCurrency } : {}),
          ...(selectedChain ? { chain: selectedChain } : {}),
        },
        CHECKOUT_ALIAS_CONFIG,
      );
      const data = res?.data ?? {};
      const redirectUrl: string | undefined = data.redirectUrl;
      const cryptoAddress: string | undefined = data.cryptoAddress;
      if (redirectUrl) {
        // Same "hand off to the gateway's own hosted page" pattern as handleSubmit's redirectUrl
        // branch: KuCoin's own page takes over from here (and may wake the KuCoin app), then
        // redirects back to our /payments/redirect/status/{transactionId} page once resolved.
        navigateForRedirect(redirectUrl);
      } else if (cryptoAddress) {
        // On-chain mode: no redirect at all. Render the deposit address on this page and start
        // polling the same status endpoint the "Continue with Card" redirect flow lands on.
        setOnchainDetails({
          transactionId: data.transactionId,
          address: cryptoAddress,
          chain: data.cryptoChain ?? null,
          currency: data.cryptoCurrencyCode ?? null,
          amount: data.cryptoAmount ?? null,
          expireTime: data.cryptoExpireTime ?? null,
          qrCodeUrl: data.cryptoQrCodeUrl ?? null,
        });
        setCryptoSubmitting(false);
      } else {
        setCryptoError(t.cryptoStartFailed);
        setCryptoSubmitting(false);
      }
    } catch (err: any) {
      setCryptoError(err.response?.data?.message || t.cryptoStartFailed);
      setCryptoSubmitting(false);
    }
  };

  // Generic Bolt-on redirect: nothing for the shopper to select, just hands off to whatever
  // hosted page that acquirer returns. Same hpp?paymentUrl:redirectUrl rule handleSubmit already
  // uses for the primary HPP case (buildPaymentProcessResponse's own convention), not
  // handleCryptoPay's plain redirectUrl (that's KuCoin's own BEMI-masked URL, set directly).
  const handleBoltOnPay = async () => {
    setBoltOnSubmitting(true);
    setBoltOnError('');
    try {
      const res: any = await apiService.post(`/checkout/orders/${token}/pay-bolt-on`, {}, CHECKOUT_ALIAS_CONFIG);
      const data = res?.data ?? {};
      const redirectUrl: string | undefined = data.hpp ? data.paymentUrl : data.redirectUrl;
      if (redirectUrl) {
        navigateForRedirect(resolveFramedRedirectUrl(redirectUrl));
      } else {
        setBoltOnError(t.paymentStartFailed);
        setBoltOnSubmitting(false);
      }
    } catch (err: any) {
      setBoltOnError(err.response?.data?.message || t.paymentStartFailed);
      setBoltOnSubmitting(false);
    }
  };

  // Pay by bank: create the payment with the chosen bank, then hand off to the provider's summary
  // page (which must be shown — it carries their licence details and terms) and on to the bank.
  // No bank chosen (the list couldn't load) is still valid: the provider then shows its own picker.
  const handleBankPay = async () => {
    setBankSubmitting(true);
    setBankError('');
    try {
      const deviceSignals = await collectDeviceSignals();
      const res: any = await apiService.post(
        `/checkout/orders/${token}/pay`,
        { ...(selectedBankId ? { selectedBank: selectedBankId } : {}), ...deviceSignals },
        { ...CHECKOUT_ALIAS_CONFIG, timeout: 60000 },
      );
      const data = res?.data ?? {};
      const redirectUrl: string | undefined = data.hpp ? data.paymentUrl : data.redirectUrl;
      if (redirectUrl) {
        navigateForRedirect(resolveFramedRedirectUrl(redirectUrl));
      } else {
        setBankError(t.paymentStartFailed);
        setBankSubmitting(false);
      }
    } catch (err: any) {
      setBankError(err.response?.data?.message || t.paymentStartFailed);
      setBankSubmitting(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setOtpError('');
    try {
      const res: any = await apiService.post(`/checkout/orders/${token}/verify-otp`, { otp: otp.trim() }, CHECKOUT_ALIAS_CONFIG);
      setAwaitingOtp(false);
      setPaymentResult(unwrapPaymentResult(res, summary));
      setPaid(true);
    } catch (err: any) {
      setAwaitingOtp(false);
      setOtp('');
      setPayError(err.response?.data?.message || t.otpFailed);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRefreshStatus = async () => {
    const transactionId = paymentResult?.transactionId;
    if (!transactionId || transactionId === '—') return;

    setRefreshing(true);
    setRefreshError('');
    try {
      const res = await apiService.get<unknown>(
        `${API_PATHS.PAYMENT_REDIRECT_STATUS}/${encodeURIComponent(transactionId)}`,
        { ...CHECKOUT_ALIAS_CONFIG, withCredentials: false },
      );
      setPaymentResult(unwrapPaymentResult(res, summary));
    } catch (err: any) {
      setRefreshError(
        err.response?.data?.message || t.refreshFailed,
      );
    } finally {
      setRefreshing(false);
    }
  };

  // On-chain crypto has no redirect/return leg to land on the status page from: this page IS
  // the status page while the shopper is sending funds, so it polls itself. Same status endpoint
  // and unwrapPaymentResult/resolvePaymentStatusVariant every other flow already uses.
  useEffect(() => {
    if (!onchainDetails || paid) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await apiService.get<unknown>(
          `${API_PATHS.PAYMENT_REDIRECT_STATUS}/${encodeURIComponent(onchainDetails.transactionId)}`,
          { ...CHECKOUT_ALIAS_CONFIG, withCredentials: false },
        );
        if (cancelled) return;
        const result = unwrapPaymentResult(res, summary);
        const variant = resolvePaymentStatusVariant(result.status);
        if (variant !== 'pending') {
          if (variant === 'success' && result.cryptoPaymentStatus === 'OVER_PAYMENT' && !overpaymentTimerStarted.current) {
            // Show "overpayment received" for a moment before flipping to the final result:
            // an instant jump would hide the fact that KuCoin actually reported an overpayment.
            overpaymentTimerStarted.current = true;
            setOverpaymentInfo({ received: result.cryptoReceivedAmount, required: result.cryptoRequiredAmount });
            window.setTimeout(() => {
              if (cancelled) return;
              setPaymentResult(result);
              setPaid(true);
            }, 5000);
            return;
          }
          if (!overpaymentTimerStarted.current) {
            setPaymentResult(result);
            setPaid(true);
          }
          return;
        }
        // Still pending overall, but KuCoin may have already reported a partial/wrong payment
        // against the deposit address (transaction held at PROCESSING): surface that instead of
        // a generic "waiting for payment" spinner, without redirecting anywhere.
        if (result.cryptoPaymentStatus === 'PART_PAYMENT' || result.cryptoPaymentStatus === 'WRONG_PAYMENT') {
          setUnderpaymentInfo({
            status: result.cryptoPaymentStatus,
            received: result.cryptoReceivedAmount,
            required: result.cryptoRequiredAmount,
          });
        } else {
          setUnderpaymentInfo(null);
        }
      } catch {
        // Best-effort polling: a transient failure just waits for the next tick.
      }
    };
    const interval = window.setInterval(poll, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [onchainDetails, paid, summary]);

  // The initial pay/verify-otp response never includes merchantRedirectUrl (only the
  // status-endpoint response does (see unwrapPaymentResult)) without this immediate follow-up
  // fetch, the auto-redirect below never had a URL to use, regardless of the timer/field-name
  // fixes, since redirectConfig would always resolve to undefined.
  const hasFetchedPostPaymentStatusRef = useRef(false);
  useEffect(() => {
    if (!paid) {
      hasFetchedPostPaymentStatusRef.current = false;
      return;
    }
    if (hasFetchedPostPaymentStatusRef.current) return;
    hasFetchedPostPaymentStatusRef.current = true;
    handleRefreshStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paid]);

  // Documented in ApiDocumentation.tsx's "Iframe Embed" section (?parentOrigin=... + a
  // moneyeu.checkout.result postMessage) but was never actually wired up here: confirmed live
  // with a partner integration (face-switch-ai) whose own listener follows the docs exactly and
  // was never receiving anything. Fires once, only when actually embedded (window.self !==
  // window.top) and only once the result is final (not "pending": 3DS/OTP challenges stay inside
  // the iframe per the docs, the message only fires once that resolves), targeted at parentOrigin
  // specifically rather than "*" so a malicious embedder can't read the payload.
  const hasPostedIframeResultRef = useRef(false);
  useEffect(() => {
    if (!paid || window.self === window.top) return;
    if (hasPostedIframeResultRef.current) return;

    const parentOrigin = new URLSearchParams(window.location.search).get('parentOrigin');
    if (!parentOrigin) return;

    const variant = paymentResult ? resolvePaymentStatusVariant(paymentResult.status) : 'success';
    if (variant === 'pending') return;

    hasPostedIframeResultRef.current = true;
    try {
      window.parent.postMessage(
        {
          type: 'moneyeu.checkout.result',
          status: paymentResult?.status ?? 'COMPLETED',
          outcome: variant === 'success' ? 'success' : 'error',
          transactionId: paymentResult?.transactionId ?? null,
          amount: paymentResult?.amount ?? summary?.amount ?? null,
          currency: paymentResult?.currency ?? summary?.currency ?? null,
          message: paymentResult?.message ?? null,
          reason: paymentResult?.reason ?? null,
          acquirerReference: paymentResult?.acquirerReference ?? null,
        },
        parentOrigin,
      );
    } catch {
      // Malformed/mismatched parentOrigin: nothing more we can do safely.
    }
  }, [paid, paymentResult, summary]);

  const formattedAmount = summary
    ? new Intl.NumberFormat(checkoutNumberLocale(locale), checkoutAmountDigits(summary.amount)).format(summary.amount)
    : '';

  const formattedMoney = summary
    ? formatCheckoutMoney(summary.amount, summary.currency, {
        locale: checkoutNumberLocale(locale) ?? 'en',
      })
    : '';

  const merchantDisplayName =
    summary?.descriptorName ?? summary?.merchantName ?? null;

  const handleCancel = () => {
    if (embedded) {
      postCheckoutMessage({ type: 'CHECKOUT_CANCEL' });
      return;
    }
    if (window.history.length > 1) {
      window.history.back();
      return;
    }
    window.close();
  };

  // Card is always an option; crypto and each APM scheme only show up when the order actually
  // offers them. A single dropdown covers all three payment-method concepts (plain card, KuCoin
  // crypto, CollectNExchange APM) rather than stacking separate selectors for each: only one of
  // them is realistically ever configured for a given order anyway.
  const hasApmSchemes = Boolean(summary?.availablePaymentSchemes && summary.availablePaymentSchemes.length > 0);
  // Hidden when the pinned/primary terminal is itself KuCoin Pay (no "card" concept at all) OR
  // when the pinned terminal has APM enabled AND actually has at least one scheme to show instead
  //: per explicit merchant request, an APM-enabled terminal shows only its APM scheme tiles, never
  // a Card option alongside them. Requiring hasApmSchemes too avoids leaving checkout with zero
  // payment options if the scheme catalog has nothing for this order's currency. Does NOT hide
  // Card when availablePaymentSchemes is populated purely from a Bolt-on's own APM terminal
  // (apmEnabled reflects the PRIMARY terminal only): that's a real, unrelated card acquirer and
  // should keep its Card tile alongside the Bolt-on's schemes.
  const cardHiddenForApm = Boolean(summary?.apmEnabled) && hasApmSchemes;
  const paymentMethodOptions = [
    ...(summary?.bankSelectionAvailable ? [{ value: 'bank', label: t.bankMethod }] : []),
    ...(summary?.cardAvailable !== false && !cardHiddenForApm
      ? [{ value: 'card', label: t.cardMethod }]
      : []),
    ...(summary?.cryptoPaymentAvailable ? [{ value: 'crypto', label: t.cryptoMethod }] : []),
    ...(summary?.availablePaymentSchemes ?? []).map((scheme) => ({ value: scheme.slug, label: scheme.label })),
    ...(summary?.boltOnAvailable ? [{ value: 'bolt_on', label: t.alternativePayment }] : []),
  ];

  const handlePaymentMethodChange = (value: string) => {
    if (value === 'crypto') {
      setPaymentMethod('crypto');
      setSelectedScheme(null);
    } else if (value === 'bolt_on') {
      setPaymentMethod('bolt_on');
      setSelectedScheme(null);
    } else if (value === 'bank') {
      setPaymentMethod('bank');
      setSelectedScheme(null);
    } else if (value === 'card') {
      setPaymentMethod('card');
      setSelectedScheme(null);
    } else {
      setPaymentMethod('card');
      setSelectedScheme(value);
    }
  };

  const currentMethodValue = selectedScheme ?? paymentMethod;
  const hostedPaySheet =
    !inlineRapyd &&
    currentMethodValue !== 'crypto' &&
    currentMethodValue !== 'bank' &&
    currentMethodValue !== 'bolt_on' &&
    !(currentMethodValue === 'card' && summary?.onrampAvailable);
  /** Card + billing side by side, matching the wide checkout. Only when this page actually
   * collects billing on the card form — redirect, onramp, and APM flows stay single column. */
  const showBillingColumns = Boolean(
    summary?.collectCustomerDetailsOnCheckout &&
    !summary?.onrampAvailable &&
    currentMethodValue === 'card' &&
    !cardRedirectOnly &&
    !inlineRapyd,
  );

  // Same content regardless of whether it's shown inline (only "Card" available, no rail needed)
  // or inside the content panel next to the method rail: one method's body, given its value.
  const renderMethodBody = (value: string) => {
    // Collect Group inline: once the pay call has created the order, Rapyd's card frame takes over
    // this card entirely — no form, no our-side Pay button (Rapyd renders its own).
    if (inlineRapyd) {
      return (
        <RapydInlineCheckout
          transactionId={inlineRapyd.transactionId}
          paymentUrl={inlineRapyd.paymentUrl}
        />
      );
    }
    // 3thix onramp rendered inline on this page instead of redirecting to pay.3thix.com.
    // First slice: proves our backend leg works and Stripe's Crypto Onramp SDK initialises on our
    // own origin. Link verification, KYC and card entry are built on top of the coordinator once a
    // real browser run confirms what it hands back.
    if (value === 'card' && summary?.onrampAvailable) {
      // Link identity step. Registration needs name/phone/country; sign-in needs only the OTP,
      // which Stripe mounts into #onramp-otp itself — we never see the code.
      const needsReg = onramp.start?.needsRegistration;
      const countries = onramp.start?.onrampCountries ?? [];
      if (onramp.phase === 'ready' || onramp.phase === 'registering'
          || onramp.phase === 'authenticating' || onramp.phase === 'authenticated'
          || onramp.phase === 'collecting-kyc' || onramp.phase === 'submitting-kyc'
          || onramp.phase === 'collecting-profile' || onramp.phase === 'kyc-complete'
          || onramp.phase === 'collecting-card' || onramp.phase === 'card-added'
          || onramp.phase === 'verifying-document' || onramp.phase === 'review-pending'
          || onramp.phase === 'paying' || onramp.phase === 'paid') {
        return (
          <div className="checkout-crypto-panel">
            {onramp.phase === 'paid' ? (
              <p>Payment complete.</p>
            ) : onramp.phase === 'paying' ? (
              <p>Completing your purchase&hellip;</p>
            ) : onramp.phase === 'card-added' ? (
              <p>Card saved. Review the amount and confirm.</p>
            ) : onramp.phase === 'collecting-card' ? (
              <p>Enter your card details below.</p>
            ) : onramp.phase === 'collecting-profile' ? (
              <>
                <p className="checkout-onramp-section">Verify your identity</p>
                <p className="checkout-onramp-intro">
                  Your details are checked securely and are not stored on this page.
                </p>
                <div className="checkout-onramp-grid">
                  <div className="checkout-field">
                    <label htmlFor="onramp-given">Given name</label>
                    <Input id="onramp-given" autoComplete="given-name" value={profile.given_name}
                      onChange={(e) => setProfile({ ...profile, given_name: e.target.value })} />
                  </div>
                  <div className="checkout-field">
                    <label htmlFor="onramp-surname">Surname</label>
                    <Input id="onramp-surname" autoComplete="family-name" value={profile.surname}
                      onChange={(e) => setProfile({ ...profile, surname: e.target.value })} />
                  </div>
                  <div className="checkout-field">
                    <label htmlFor="onramp-dob">Date of birth</label>
                    <Input id="onramp-dob" type="date" autoComplete="bday" value={profile.dob}
                      onChange={(e) => setProfile({ ...profile, dob: e.target.value })} />
                  </div>
                  {/* The ADDRESS country, which is not necessarily the country picked at Link
                      registration. Taking it from that dropdown meant a New York address could be
                      submitted with country=ES, which Stripe rejects as a region mismatch. */}
                  <div className="checkout-field">
                    <label htmlFor="onramp-country">Country of residence</label>
                    <select id="onramp-country" className="checkout-select"
                      value={profile.country || onrampForm.country}
                      onChange={(e) => setProfile({ ...profile, country: e.target.value })}>
                      <option value="">Select country</option>
                      {countries.map((c) => (
                        <option key={c.code} value={c.code}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="checkout-field checkout-field--full">
                    <label htmlFor="onramp-line1">Address</label>
                    <Input id="onramp-line1" autoComplete="address-line1" value={profile.line1}
                      onChange={(e) => setProfile({ ...profile, line1: e.target.value })} />
                  </div>
                  <div className="checkout-field">
                    <label htmlFor="onramp-city">City</label>
                    <Input id="onramp-city" autoComplete="address-level2" value={profile.city}
                      onChange={(e) => setProfile({ ...profile, city: e.target.value })} />
                  </div>
                  <div className="checkout-field">
                    <label htmlFor="onramp-state">
                      {['US', 'CA', 'BR'].includes(profile.country || onrampForm.country || 'US')
                        ? 'State' : 'State / region'}
                    </label>
                    <Input id="onramp-state" autoComplete="address-level1" value={profile.state}
                      onChange={(e) => setProfile({ ...profile, state: e.target.value })} />
                    {['US', 'CA', 'BR'].includes(profile.country || onrampForm.country || 'US') && (
                      <p className="checkout-hint">Two-letter code, for example CA</p>
                    )}
                  </div>
                  <div className="checkout-field">
                    <label htmlFor="onramp-postal">Postal code</label>
                    <Input id="onramp-postal" autoComplete="postal-code" value={profile.postal_code}
                      onChange={(e) => setProfile({ ...profile, postal_code: e.target.value })} />
                  </div>
                  {/* US only. Stripe needs the FULL 9 digits here — last-4 is silently rejected,
                      which looks like success and then stalls. */}
                  {(profile.country || onrampForm.country) === 'US' && (
                    <div className="checkout-field">
                      <label htmlFor="onramp-ssn">Social Security number</label>
                      <Input id="onramp-ssn" inputMode="numeric" value={profile.ssn}
                        onChange={(e) => setProfile({ ...profile, ssn: e.target.value })} />
                      <p className="checkout-hint">All nine digits</p>
                      {/* Shown because being asked for an SSN mid-payment reads as suspicious
                          without an explanation, and shoppers abandon rather than ask. */}
                      <p className="checkout-hint">
                        SSN is used as a proof of humanness to help verify the customer's identity
                        and prevent fraud. An SSN is not required for every payment.
                      </p>
                    </div>
                  )}
                </div>
              </>
            ) : onramp.phase === 'kyc-complete' ? (
              <p>Verification complete. Payment details are the next step.</p>
            ) : onramp.phase === 'collecting-kyc' || onramp.phase === 'submitting-kyc' ? (
              <>
                <p className="checkout-onramp-section">A few more details</p>
                {/* Rendered from what Stripe asked for — the field set differs by country
                    (kycRegion us/uk/eu), so nothing here is hardcoded. */}
                <div className="checkout-onramp-grid">
                  {(onramp.missing?.identifiers ?? []).map((ident) => (
                    <div className="checkout-field" key={ident.type}>
                      <label htmlFor={`onramp-id-${ident.type}`}>
                        {ident.type
                          .replace(/_/g, ' ')
                          .replace(/\b\w/g, (ch) => ch.toUpperCase())}
                      </label>
                      <Input
                        id={`onramp-id-${ident.type}`}
                        value={kycValues[ident.type] ?? ''}
                        onChange={(e) =>
                          setKycValues({ ...kycValues, [ident.type]: e.target.value })}
                      />
                    </div>
                  ))}
                </div>
              </>
            ) : onramp.phase === 'verifying-document' || onramp.phase === 'review-pending' ? (
              <p className="checkout-onramp-section">Identity check</p>
            ) : onramp.phase === 'authenticated' ? (
              <>
                <p className="checkout-onramp-section">You&rsquo;re signed in</p>
                <p className="checkout-onramp-intro">
                  Next we need a few details to verify your identity, as required for this
                  payment type.
                </p>
              </>
            ) : needsReg ? (
              <>
                <p className="checkout-onramp-section">Create your payment profile</p>
                <p className="checkout-onramp-intro">
                  We&rsquo;ll text you a code to confirm it&rsquo;s you.
                </p>
                <div className="checkout-onramp-grid">
                  <div className="checkout-field checkout-field--full">
                    <label htmlFor="onramp-legal-name">Full name</label>
                    <Input id="onramp-legal-name" autoComplete="name" value={onrampForm.legalName}
                      onChange={(e) => setOnrampForm({ ...onrampForm, legalName: e.target.value })} />
                  </div>
                  <div className="checkout-field">
                    <label htmlFor="onramp-reg-country">Country</label>
                    <select id="onramp-reg-country" className="checkout-select" value={onrampForm.country}
                      onChange={(e) => setOnrampForm({ ...onrampForm, country: e.target.value })}>
                      <option value="">Select country</option>
                      {countries.map((c) => (
                        <option key={c.code} value={c.code}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="checkout-field">
                    <label htmlFor="onramp-phone">Phone number</label>
                    <Input id="onramp-phone" type="tel" autoComplete="tel"
                      value={onrampForm.phoneNumber}
                      onChange={(e) => setOnrampForm({ ...onrampForm, phoneNumber: e.target.value })} />
                  </div>
                </div>
              </>
            ) : (
              <p>Enter the code we just sent you.</p>
            )}
            {/* authenticate() returns a detached div holding Stripe's OTP iframe; the hook
                appends it here. Nothing renders if this host is missing. */}
            <div className="checkout-onramp-host" ref={onramp.otpHostRef} style={{ overflow: 'hidden', height: '340px' }} />
            {/* Same contract for the card form — Stripe's iframe, so the card number is never
                in our DOM. */}
            <div className="checkout-onramp-host" ref={onramp.cardHostRef} />

            {/* Stripe opens document capture in its own window, so the page needs to say why it
                went quiet — an unexplained pause here reads as a hang. */}
            {onramp.phase === 'verifying-document' && (
              <p>Follow the identity check in the window that just opened. It asks for a photo of
                a government ID and a selfie. This purchase needs it before it can complete.</p>
            )}
            {onramp.phase === 'review-pending' && (
              <>
                <p>Your documents were submitted and are being reviewed. This usually takes a few
                  minutes — keep this page open and try again shortly.</p>
                <button type="button" className="checkout-pay-btn"
                  onClick={() => void onramp.payNow()}>
                  Try again
                </button>
              </>
            )}
            {onramp.phase === 'kyc-complete' && (
              <button type="button" className="checkout-pay-btn"
                onClick={() => void onramp.collectCard()}>
                Add payment method
              </button>
            )}
            {onramp.phase === 'card-added' && (
              <button type="button" className="checkout-pay-btn"
                onClick={() => void onramp.payNow()}>
                Pay {formatCheckoutMoney(summary?.amount, summary?.currency, {
                  includeCode: Boolean(summary?.currency),
                })}
              </button>
            )}
            {onramp.phase === 'authenticated' && (
              <button type="button" className="checkout-pay-btn"
                onClick={() => onramp.beginProfile()}>
                Continue
              </button>
            )}
            {onramp.phase === 'collecting-profile' && (
              <button type="button" className="checkout-pay-btn"
                onClick={async () => {
                  const [y, m, d] = (profile.dob || '').split('-');
                  const country = profile.country || onrampForm.country || 'US';
                  // Stripe wants a 2-letter code for these, not a region name — sending
                  // "Barcelona" with country=US is rejected as "Invalid value for parameter
                  // address_state". Same rule 3thix's own client applies.
                  const stateVal = profile.state.trim().toUpperCase();
                  if (['US', 'CA', 'BR'].includes(country) && stateVal
                      && !/^[A-Z]{2}$/.test(stateVal)) {
                    window.alert(
                      `Please enter the 2-letter ${country === 'CA' ? 'province' : 'state'} code `
                      + `(for example, ${country === 'CA' ? 'ON' : 'CA'}).`);
                    return;
                  }
                  if (country === 'US'
                      && !/^\d{3}-?\d{2}-?\d{4}$/.test(profile.ssn.trim())) {
                    window.alert('Please enter your full 9-digit Social Security number.');
                    return;
                  }
                  const ok = await onramp.submitProfile({
                    given_name: profile.given_name.trim(),
                    surname: profile.surname.trim(),
                    address: {
                      line1: profile.line1.trim(),
                      city: profile.city.trim(),
                      postal_code: profile.postal_code.trim(),
                      country,
                      ...(stateVal ? { state: stateVal } : {}),
                    },
                    date_of_birth: {
                      year: parseInt(y, 10), month: parseInt(m, 10), day: parseInt(d, 10),
                    },
                    ...(country === 'US' && profile.ssn
                      ? { id_number: { value: profile.ssn.replace(/-/g, ''), type: 'us_ssn' } }
                      : {}),
                  });
                  if (ok) void onramp.loadKycRequirements(country);
                }}>
                Submit details
              </button>
            )}
            {(onramp.phase === 'collecting-kyc' || onramp.phase === 'submitting-kyc') && (
              <button type="button" className="checkout-pay-btn"
                disabled={onramp.phase === 'submitting-kyc'}
                onClick={() => void onramp.submitIdentifiers(
                  (onramp.missing?.identifiers ?? []).map((i) => ({
                    type: i.type, value: kycValues[i.type] ?? '',
                  })))}>
                {onramp.phase === 'submitting-kyc' ? 'Verifying\u2026' : 'Submit'}
              </button>
            )}
            {/* Allow-list, not an exclusion list. This was written as "every phase except
                these four", so it also rendered a second, wrong "Verify identity" button during
                card entry, payment and the document step — the identity stage is the only place
                it belongs. */}
            {(onramp.phase === 'ready' || onramp.phase === 'registering'
              || onramp.phase === 'authenticating') && (
              <button type="button" className="checkout-pay-btn"
                disabled={onramp.phase === 'registering' || onramp.phase === 'authenticating'}
                onClick={() => void onramp.authenticateLink(onrampForm)}>
                {onramp.phase === 'registering' || onramp.phase === 'authenticating'
                  ? 'Verifying\u2026' : 'Verify identity'}
              </button>
            )}
            {onramp.error && <p className="checkout-error">{onramp.error}</p>}
          </div>
        );
      }

      return (
        <div className="checkout-crypto-panel">
          <p>
            {/* 'ready' and later are handled by the identity block above, so only the
                pre-start phases reach here. */}
            {onramp.phase === 'error'
              ? onramp.error
              : onramp.phase === 'idle'
                ? 'Continue to pay securely.'
                : 'Connecting securely\u2026'}
          </p>
          <button
            type="button"
            className="checkout-pay-btn"
            disabled={onramp.phase !== 'idle' && onramp.phase !== 'error'}
            onClick={() => void onramp.begin(undefined)}
          >
            {onramp.phase === 'error' ? 'Try again' : 'Continue'}
          </button>
          {/* Visible during bring-up so a failure is diagnosable from the page itself rather than
              only from the console. */}
        </div>
      );
    }

    if (value === 'crypto') {
      const isOnchain = summary?.cryptoPaymentMode === 'ONCHAIN';

      if (isOnchain && onchainDetails) {
        return (
          <div className="checkout-crypto-panel">
            {onchainDetails.qrCodeUrl && (
              <div className="checkout-crypto-qr-wrap">
                <img src={onchainDetails.qrCodeUrl} alt={t.scanToPay} />
              </div>
            )}
            <p className="checkout-crypto-amount-line">
              {t.sendExactly}{' '}
              <span className="checkout-crypto-amount-value">
                {onchainDetails.amount} {onchainDetails.currency}
              </span>
              {onchainDetails.chain && (
                <span className="checkout-crypto-network-badge">{onchainDetails.chain.toUpperCase()}</span>
              )}
            </p>
            <div className="checkout-crypto-address-box">{onchainDetails.address}</div>
            <button
              type="button"
              className={`checkout-crypto-copy-btn${onchainCopied ? ' copied' : ''}`}
              onClick={() => {
                navigator.clipboard?.writeText(onchainDetails.address).then(() => {
                  setOnchainCopied(true);
                  window.setTimeout(() => setOnchainCopied(false), 2000);
                });
              }}
            >
              {onchainCopied ? t.copied : t.copyAddress}
            </button>
            {onchainDetails.expireTime && (() => {
              const countdown = formatCountdown(onchainDetails.expireTime, onchainNow);
              return (
                <p className="checkout-crypto-expiry">
                  {countdown ? t.expiresIn(countdown) : t.depositExpired}
                </p>
              );
            })()}
            {overpaymentInfo ? (
              <div className="checkout-crypto-overpayment-banner">
                {t.overpayment(overpaymentInfo.received ?? '', onchainDetails.currency ?? '')}
              </div>
            ) : underpaymentInfo ? (
              <div className="checkout-crypto-underpayment-banner">
                {underpaymentInfo.status === 'WRONG_PAYMENT' ? (
                  <>{t.wrongPayment(underpaymentInfo.received ?? '', onchainDetails.currency ?? '')}</>
                ) : (
                  <>{t.underpayment(underpaymentInfo.received ?? '', onchainDetails.currency ?? '')}{' '}
                    {(() => {
                      const remaining = formatRemainingAmount(underpaymentInfo.required, underpaymentInfo.received);
                      return remaining
                        ? t.sendRemainingExact(remaining, onchainDetails.currency ?? '')
                        : t.sendRemainingGeneric;
                    })()}
                  </>
                )}
              </div>
            ) : (
              <div className="checkout-crypto-waiting">
                <BsArrowRepeat className="checkout-spin-icon" /> {t.waitingForPayment}
              </div>
            )}
          </div>
        );
      }

      const currencyOptions = summary?.cryptoOnchainCurrencyOptions ?? {};
      const currencyKeys = Object.keys(currencyOptions);
      const chainOptions = selectedCurrency ? currencyOptions[selectedCurrency] ?? [] : [];

      return (
        <div className="checkout-crypto-panel">
          <p className="checkout-crypto-message">
            {isOnchain ? t.cryptoOnchainHint : t.cryptoOffchainHint}
          </p>
          {isOnchain && currencyKeys.length > 1 && (
            <div className="checkout-field" style={{ width: '100%', textAlign: 'left' }}>
              <label htmlFor="checkout-currency-select">{t.currency}</label>
              <select
                id="checkout-currency-select"
                className="checkout-select"
                value={selectedCurrency ?? ''}
                onChange={(e) => setSelectedCurrency(e.target.value)}
              >
                {currencyKeys.map((currency) => (
                  <option key={currency} value={currency}>
                    {currency.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
          )}
          {isOnchain && chainOptions.length > 1 && (
            <div className="checkout-field" style={{ width: '100%', textAlign: 'left' }}>
              <label htmlFor="checkout-network-select">{t.network}</label>
              <select
                id="checkout-network-select"
                className="checkout-select"
                value={selectedChain ?? ''}
                onChange={(e) => setSelectedChain(e.target.value)}
              >
                {chainOptions.map((chain: string) => (
                  <option key={chain} value={chain}>
                    {chain.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
          )}
          {cryptoError && <div className="checkout-error-banner">{cryptoError}</div>}
          <button
            type="button"
            className="checkout-pay-btn"
            onClick={handleCryptoPay}
            disabled={cryptoSubmitting}
          >
            {cryptoSubmitting ? (
              <>
                <BsArrowRepeat className="checkout-spin-icon" /> {isOnchain ? t.loading : t.redirecting}
              </>
            ) : (
              <>
                <BsCurrencyBitcoin /> {isOnchain ? t.getDepositAddress : t.continueWithCrypto}
              </>
            )}
          </button>
        </div>
      );
    }

    if (value === 'bank') {
      const query = bankQuery.trim().toLowerCase();
      const visibleBanks = (banks ?? []).filter(
        (b) => !query || b.name.toLowerCase().includes(query) || (b.bankGroupName ?? '').toLowerCase().includes(query),
      );
      return (
        <div className="checkout-crypto-panel checkout-bank-panel">
          <p className="checkout-crypto-message">{t.bankRedirectNote}</p>
          {banks === null ? (
            <p className="checkout-crypto-message">
              <BsArrowRepeat className="checkout-spin-icon" /> {t.loading}
            </p>
          ) : banksFailed ? (
            <p className="checkout-crypto-message">{t.bankListUnavailable}</p>
          ) : (
            <>
              <label className="checkout-bank-label" htmlFor="checkout-bank-search">
                {t.chooseBank}
              </label>
              <input
                id="checkout-bank-search"
                className="checkout-bank-search"
                type="search"
                placeholder={t.searchBank}
                value={bankQuery}
                onChange={(e) => setBankQuery(e.target.value)}
                autoComplete="off"
              />
              <div className="checkout-bank-list" role="radiogroup" aria-label={t.chooseBank}>
                {visibleBanks.length === 0 ? (
                  <p className="checkout-crypto-message">{t.noBanksFound}</p>
                ) : (
                  visibleBanks.map((bank) => (
                    <button
                      key={bank.bankId}
                      type="button"
                      role="radio"
                      aria-checked={selectedBankId === bank.bankId}
                      className={`checkout-bank-option${selectedBankId === bank.bankId ? ' is-selected' : ''}`}
                      onClick={() => setSelectedBankId(bank.bankId)}
                    >
                      {bank.name}
                    </button>
                  ))
                )}
              </div>
            </>
          )}
          {bankError && <div className="checkout-error-banner">{bankError}</div>}
          <button
            type="button"
            className="checkout-pay-btn"
            onClick={handleBankPay}
            disabled={bankSubmitting || banks === null || (!banksFailed && !selectedBankId)}
          >
            {bankSubmitting ? (
              <>
                <BsArrowRepeat className="checkout-spin-icon" /> {t.redirecting}
              </>
            ) : (
              t.payWithBank
            )}
          </button>
        </div>
      );
    }

    if (value === 'bolt_on') {
      // Nothing to select: a plain redirect to whatever the merchant's Bolt-on acquirer returns.
      // Deliberately never names the acquirer itself, matching the crypto/APM tiles' own wording.
      return (
        <div className="checkout-crypto-panel">
          <p className="checkout-crypto-message">
            {t.boltOnHint}
          </p>
          {boltOnError && <div className="checkout-error-banner">{boltOnError}</div>}
          <button
            type="button"
            className="checkout-pay-btn"
            onClick={handleBoltOnPay}
            disabled={boltOnSubmitting}
          >
            {boltOnSubmitting ? (
              <>
                <BsArrowRepeat className="checkout-spin-icon" /> {t.redirecting}
              </>
            ) : (
              t.continue
            )}
          </button>
        </div>
      );
    }

    // Card under a CollectNExchange terminal redirects too, APM enabled or not: CollectNExchange
    // collects card details on its own hosted page for every method, never ours.
    const isRedirectOnly = value !== 'card' || cardRedirectOnly;
    const redirectLabel =
      value === 'card' ? t.cardLabel : summary?.availablePaymentSchemes?.find((s) => s.slug === value)?.label ?? t.paymentLabel;

    const payButton = (
      <button type="submit" className="checkout-pay-btn" disabled={submitting}>
        {submitting ? t.processing : t.payNow}
      </button>
    );

    const cardFields = (
      <>
        <CheckoutOutlineField
          label={t.cardNumber}
          type="text"
          inputMode="numeric"
          autoComplete="cc-number"
          error={errors.cardNumber}
          value={form.cardNumber}
          onChange={(e) => set('cardNumber', formatCardNumber(e.target.value))}
          placeholder={t.cardNumberPlaceholder}
        />
        <div className="checkout-row">
          <CheckoutStackedField
            label={t.expiryDate}
            type="text"
            inputMode="numeric"
            autoComplete="cc-exp"
            error={errors.expiryDate}
            value={form.expiryDate}
            onChange={(e) => set('expiryDate', formatExpiryDate(e.target.value))}
            placeholder={t.expiryPlaceholder}
          />
          <CheckoutStackedField
            label={t.securityCode}
            type="text"
            inputMode="numeric"
            autoComplete="cc-csc"
            error={errors.cvv}
            value={form.cvv}
            onChange={(e) => set('cvv', formatCvv(e.target.value))}
            placeholder={t.cvvPlaceholder}
          />
        </div>
        <CheckoutStackedField
          label={t.cardholderName}
          type="text"
          autoComplete="cc-name"
          error={errors.cardholderName}
          value={form.cardholderName}
          onChange={(e) => set('cardholderName', e.target.value)}
          placeholder={t.cardholderPlaceholder}
        />
      </>
    );

    const billingFields = (
      <>
        <CheckoutFloatField
          label="Full name"
          type="text"
          autoComplete="name"
          error={errors.customerName}
          value={billingForm.customerName}
          onChange={(e) => setBilling('customerName', e.target.value)}
          placeholder="John Doe"
        />
        <CheckoutFloatField
          label="Address"
          type="text"
          autoComplete="street-address"
          error={errors.address}
          value={billingForm.address}
          onChange={(e) => setBilling('address', e.target.value)}
          placeholder="123 Main St"
        />
        <div className="checkout-row">
          <CheckoutFloatField
            label="City"
            type="text"
            autoComplete="address-level2"
            error={errors.city}
            value={billingForm.city}
            onChange={(e) => setBilling('city', e.target.value)}
            placeholder="City"
          />
          <CheckoutFloatField
            label="State"
            type="text"
            autoComplete="address-level1"
            error={errors.state}
            value={billingForm.state}
            onChange={(e) => setBilling('state', e.target.value)}
            placeholder="State"
          />
        </div>
        <div className="checkout-row">
          <CheckoutFloatField
            label="Zip"
            type="text"
            autoComplete="postal-code"
            error={errors.zip}
            value={billingForm.zip}
            onChange={(e) => setBilling('zip', e.target.value)}
            placeholder="Zip"
          />
          <CheckoutFloatField
            label="Country"
            type="text"
            autoComplete="country-name"
            error={errors.countryName}
            value={billingForm.countryName}
            onChange={(e) => setBilling('countryName', e.target.value)}
            placeholder="Country"
          />
        </div>
        <CheckoutFloatField
          label="Phone"
          type="text"
          autoComplete="tel"
          error={errors.phone}
          value={billingForm.phone}
          onChange={(e) => setBilling('phone', e.target.value)}
          placeholder="+1234 567 8900"
        />
      </>
    );

    const latinFields = summary?.latinSupportEnabled ? (
      <>
        <h2 className="checkout-section-title checkout-section-title--sub">Latin characters</h2>
        <CheckoutFloatField
          label="Full name (Latin characters)"
          type="text"
          error={errors.customerNameLatin}
          hint="Please fill in Latin characters."
          value={billingForm.customerNameLatin}
          onChange={(e) => setBilling('customerNameLatin', e.target.value)}
          placeholder="John Doe"
        />
        <CheckoutFloatField
          label="Address (Latin characters)"
          type="text"
          error={errors.addressLatin}
          hint="Please fill in Latin characters."
          value={billingForm.addressLatin}
          onChange={(e) => setBilling('addressLatin', e.target.value)}
          placeholder="123 Main St"
        />
      </>
    ) : null;

    return (
      <form
        onSubmit={handleSubmit}
        className={showBillingColumns ? 'checkout-form checkout-form--details' : 'checkout-form checkout-form--hosted'}
      >
        {isRedirectOnly ? (
          /* Tax ID + DOB inline-identity fields removed 2026-10-28: Collect Group now collects
             identity (tax ID / date of birth) on its own hosted page, so we no longer ask for them
             on our checkout page. The shopper is simply redirected to Collect Group's page. The
             original inline branches are preserved below — un-comment to restore them.
          summary?.collectInlineIdentity && summary?.testDummyValues ? (
            // Test dummy values: no identity fields — one click goes straight to the Rapyd card form.
            <p className="checkout-status-sub" style={{ textAlign: 'center' }}>
              Your details are entered on the next secure step.
            </p>
          ) : summary?.collectInlineIdentity ? (
            // Collect Group inline: tax id + DOB are collected here (Collect Group needs them for
            // its AML profile before the embedded card form will accept a payment). The card
            // itself is still entered on the embedded Rapyd page, so no card fields here.
            <>
              <div className="checkout-field">
                <label htmlFor="inline-tax-id">Tax ID</label>
                <Input
                  id="inline-tax-id"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  className={cn(errors.taxId && 'border-destructive')}
                  value={identityForm.taxId}
                  onChange={(e) => setIdentityForm((p) => ({ ...p, taxId: e.target.value }))}
                  placeholder="Tax identification number"
                />
                {errors.taxId && <p className="checkout-field-err">{errors.taxId}</p>}
              </div>
              <div className="checkout-field">
                <label htmlFor="inline-dob">Date of birth</label>
                <Input
                  id="inline-dob"
                  type="date"
                  className={cn(errors.dateOfBirth && 'border-destructive')}
                  value={identityForm.dateOfBirth}
                  onChange={(e) => setIdentityForm((p) => ({ ...p, dateOfBirth: e.target.value }))}
                />
                {errors.dateOfBirth && <p className="checkout-field-err">{errors.dateOfBirth}</p>}
              </div>
              <p className="checkout-hint" style={{ textAlign: 'center' }}>
                Used to verify your identity and prevent fraud. Your card details are entered on the
                next secure step.
              </p>
            </>
          ) :
          */
          (
            <p className="checkout-status-sub" style={{ textAlign: 'center' }}>
              {t.redirectCard(redirectLabel)}
            </p>
          )
        ) : showBillingColumns ? (
          <div className="checkout-details">
            <div className="checkout-details-col">
              <h2 className="checkout-section-title">Card</h2>
              {cardFields}
              {latinFields}
            </div>
            <div className="checkout-details-col">
              <h2 className="checkout-section-title">Billing</h2>
              {billingFields}
              {payError && <div className="checkout-error-banner">{payError}</div>}
              <CheckoutTrustRow label={t.securePayments} />
              {payButton}
            </div>
          </div>
        ) : (
          cardFields
        )}

        {!showBillingColumns && <CheckoutTrustRow label={t.securePayments} />}
        {!showBillingColumns && (
          <div className="checkout-pay-dock">
            {payError && <div className="checkout-error-banner">{payError}</div>}
            {payButton}
          </div>
        )}
      </form>
    );
  };

  if (loadState === 'loading') {
    return (
      <div className="checkout-screen text-foreground" data-theme="light" lang={locale} dir={checkoutDir(locale)}>
        <div className="checkout-card checkout-status-card">
          <div className="checkout-spinner" />
          <p className="checkout-status-sub">{t.loadingDetails}</p>
        </div>
      </div>
    );
  }

  if (loadState === 'invalid') {
    return (
      <PaymentStatusCard
        variant="error"
        title={t.linkUnavailable}
        message={loadError}
        logoUrl={null}
        embedded={embedded}
        locale={locale}
      />
    );
  }

  if (summary && isTerminalCheckoutOutcome(summary.outcome)) {
    const variant = resolvePaymentStatusVariant(summary.outcome || summary.status);
    const formattedOrderAmount = formatCheckoutMoney(summary.amount, summary.currency, {
      includeCode: Boolean(summary.currency),
      locale: checkoutNumberLocale(locale) ?? 'en',
    });

    return (
      <PaymentStatusCard
        variant={variant}
        transactionId={summary.orderId || undefined}
        amount={formattedOrderAmount}
        statusText={summary.status}
        logoUrl={null}
        embedded={embedded}
        locale={locale}
      />
    );
  }

  if (paid) {
    const variant = paymentResult
      ? resolvePaymentStatusVariant(paymentResult.status)
      : 'success';

    const amountValue = paymentResult?.amount ?? summary?.amount ?? null;
    const currencyValue = paymentResult?.currency ?? summary?.currency ?? '';
    const formattedResultAmount =
      amountValue != null
        ? formatCheckoutMoney(amountValue, currencyValue, {
            includeCode: Boolean(currencyValue),
            locale: checkoutNumberLocale(locale) ?? 'en',
          })
        : undefined;

    const transactionId =
      paymentResult?.transactionId && paymentResult.transactionId !== '—'
        ? paymentResult.transactionId
        : undefined;

    const redirectConfig = embedded
      ? undefined
      : resolvePostPaymentRedirect(paymentResult, variant);

    const canRefresh = Boolean(transactionId);

    return (
      <PaymentStatusCard
        variant={variant}
        message={refreshError || undefined}
        transactionId={transactionId}
        amount={formattedResultAmount}
        statusText={paymentResult?.status ?? t.submitted}
        reason={paymentResult?.reason || undefined}
        redirect={redirectConfig}
        logoUrl={paymentResult?.logoUrl}
        embedded={embedded}
        locale={locale}
        action={
          canRefresh
            ? {
                label: refreshing ? t.refreshing : t.refresh,
                hint: t.refreshHint,
                onClick: handleRefreshStatus,
                disabled: refreshing,
                autoTriggerAfterMs: variant === 'pending' ? 10000 : undefined,
              }
            : undefined
        }
      />
    );
  }

  if (awaitingOtp) {
    return (
      <div className="checkout-screen text-foreground" data-theme="light" lang={locale} dir={checkoutDir(locale)}>
        <div className="checkout-card">
          <div className={`checkout-brand${checkoutBrandClass(locale)}`}>
            <BsShieldLockFill /> {t.verifyPayment}
          </div>
          <p className="checkout-status-sub" style={{ textAlign: 'center', marginBottom: '18px' }}>
            {t.otpHint}
          </p>
          <form onSubmit={handleVerifyOtp} className="checkout-form">
            <div className="checkout-field">
              <label>{t.otpLabel}</label>
              <div className="relative">
                <BsLockFill className={fieldIconClassName} />
                <Input
                  type="text"
                  inputMode="numeric"
                  className="pl-9"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder={t.otpPlaceholder}
                  autoFocus
                />
              </div>
            </div>
            {otpError && <div className="checkout-error-banner">{otpError}</div>}
            <button type="submit" className="checkout-pay-btn" disabled={submitting}>
              <BsShieldLockFill /> {submitting ? t.verifying : t.verifyAndPay}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // The method picker, active method and footer. Rendered BARE for every integration except the
  // inline 3thix onramp, which alone needs it wrapped as the right-hand pane of the split layout.
  // One value, so the two paths cannot drift apart — and no wrapper element exists at all on the
  // ordinary card, APM, crypto or bolt-on checkout.
  const checkoutBody = (
    <>
            {paymentMethodOptions.length > 1 && (
              <div className="checkout-method-grid" role="radiogroup" aria-label={t.paymentMethod}>
                {paymentMethodOptions.map((opt) => {
                  const isActive = currentMethodValue === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="radio"
                      aria-checked={isActive}
                      className={`checkout-method-tile ${isActive ? 'active' : ''}`}
                      onClick={() => handlePaymentMethodChange(opt.value)}
                    >
                      {isActive && <span className="checkout-method-tile-check">✓</span>}
                      <span className="checkout-method-tile-label">{opt.label}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {renderMethodBody(currentMethodValue)}

            {summary?.terminalNotes && summary.terminalNotes.trim() && (
              <div className="checkout-notice-footer" role="note">
                <BsExclamationTriangleFill />
                <span>{summary.terminalNotes.trim()}</span>
              </div>
            )}

            {!onrampFormOpen && !showBillingColumns && !inlineRapyd && !hostedPaySheet && (
              <CheckoutTrustRow label={t.securePayments} />
            )}
    </>
  );

  return (
    <div className="checkout-screen text-foreground" data-theme="light" lang={locale} dir={checkoutDir(locale)}>
      {redirecting && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2147483647,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '14px',
            background: 'rgba(255, 255, 255, 0.96)',
            color: '#111',
            textAlign: 'center',
            padding: '24px',
          }}
        >
          <BsArrowRepeat className="checkout-spin-icon" style={{ fontSize: '34px' }} />
          <div style={{ fontSize: '15px', fontWeight: 500 }}>{t.redirecting}</div>
        </div>
      )}
      <div className="checkout-frame">
        {/* Two-panel layout for the inline identity flow: the order sits in a fixed rail on the
            left and the active step occupies the right, so a nine-field form reads across the page
            instead of down it. The single-column card is kept for the ordinary card/APM checkout,
            where there is only ever one short form and a rail would be empty ceremony. */}
        <div className={`checkout-card${onrampFormOpen ? ' checkout-card--split' : ' checkout-card--hosted'}${inlineRapyd ? ' checkout-card--rapyd' : ''}${showBillingColumns ? ' checkout-card--details' : ''}`}>
          {onrampFormOpen && summary ? (
            <aside className="checkout-rail">
              <div className={`checkout-brand${checkoutBrandClass(locale)}`}>
                <BsShieldLockFill /> {t.secureCheckout}
              </div>
              <div className="checkout-rail-body">
                <p className="checkout-summary-label">{t.amountDue}</p>
                <div className="checkout-summary-amount">
                  {formattedAmount}
                  <span>{summary.currency}</span>
                </div>
                {summary.description && (
                  <p className="checkout-summary-desc">{summary.description}</p>
                )}
                {/* The configured trading name replaces the account name when set; otherwise the
                    page is unchanged for every merchant that hasn't turned this on. */}
                {merchantDisplayName && (
                  <p className="checkout-summary-merchant">
                    {t.toMerchant(merchantDisplayName)}
                  </p>
                )}
              </div>
              <div className="checkout-rail-foot">
                <BsShieldLockFill /> {t.footerNote}
              </div>
            </aside>
          ) : (
            <div className="checkout-hosted-bar">
              <button type="button" className="checkout-cancel" onClick={handleCancel}>
                {t.cancel}
                <span className="checkout-cancel-x" aria-hidden="true">×</span>
              </button>
              <div className="checkout-amount-row">
                <p className="checkout-amount-line">
                  {t.amountLabel}: <strong>{formattedMoney}</strong>
                </p>
                <button
                  type="button"
                  className="checkout-order-toggle"
                  aria-expanded={orderDetailsOpen}
                  onClick={() => setOrderDetailsOpen((open) => !open)}
                >
                  {t.orderDetails}
                  <svg className={`checkout-order-chevron${orderDetailsOpen ? ' is-open' : ''}`} width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                    <path d="M2.2 4.4 6 8l3.8-3.6" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
              {orderDetailsOpen && (
                <div className="checkout-order-panel">
                  {summary?.orderId ? (
                    <div className="checkout-order-id">
                      <span className="checkout-order-id-label">{t.orderIdLabel}</span>
                      <span className="checkout-order-id-value">{summary.orderId}</span>
                      <button
                        type="button"
                        className={`checkout-order-copy${orderIdCopied ? ' is-copied' : ''}`}
                        aria-label={orderIdCopied ? t.copiedShort : t.copy}
                        title={orderIdCopied ? t.copiedShort : t.copy}
                        onClick={() => {
                          void navigator.clipboard?.writeText(summary.orderId).then(() => {
                            setOrderIdCopied(true);
                            window.setTimeout(() => setOrderIdCopied(false), 2000);
                          }).catch(() => {});
                        }}
                      >
                        {orderIdCopied ? (
                          <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
                            <path d="M3.2 8.2 6.4 11.4 12.8 4.6" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        ) : (
                          <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
                            <rect x="5.2" y="5.2" width="8" height="8" rx="1.4" fill="none" stroke="currentColor" strokeWidth="1.4" />
                            <path d="M10.8 5.2V3.4A1.4 1.4 0 0 0 9.4 2H3.4A1.4 1.4 0 0 0 2 3.4v6A1.4 1.4 0 0 0 3.4 10.8H5.2" fill="none" stroke="currentColor" strokeWidth="1.4" />
                          </svg>
                        )}
                      </button>
                    </div>
                  ) : (
                    <p>{t.noOrderDetails}</p>
                  )}
                </div>
              )}
              <hr className="checkout-hosted-divider" />
              {/* <div className="checkout-method-caption">
                <span>{t.paymentMethod}:</span>
                <strong>
                  {currentMethodValue === 'card' && <CardGlyph />}
                  {methodCaption}
                </strong>
              </div> */}
            </div>
          )}

          {onrampFormOpen ? <div className="checkout-pane">{checkoutBody}</div> : checkoutBody}
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
